import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Chrome";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useCart } from "../lib/cart";
import { resolveImage } from "../lib/api";
import { unitSummary, PaymentBadge, TAX_NOTICE_ELEC, TAX_NOTICE_CASH } from "../lib/orderDisplay";

export default function Cart() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { cart, setQty, remove, totals, paymentBreakdown } = useCart();
  const nav = useNavigate();
  const canOrder = settings?.ordering_enabled && user && user.role === "retailer" && user.status === "approved";
  const currency = settings?.currency || "$";

  if (!canOrder) {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <Header />
        <div className="pt-28 md:pt-32 pb-16 px-6 md:px-12 lg:px-16 max-w-3xl">
          <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">Cart.</h1>
          <div className="mt-6 border border-ink p-6" data-testid="cart-disabled">
            <p className="text-sm">
              {!user ? "Log in as an approved retailer to build a cart and place wholesale orders."
                : user.role !== "retailer" ? "Only retailer accounts can place orders."
                : user.status !== "approved" ? "Your retailer application is still pending review."
                : "Online ordering is currently disabled. Please contact us to place an order."}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {!user && <Link to="/login" className="btn-primary">Retailer login</Link>}
              <Link to="/catalog" className="btn-ghost">Back to catalog</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const items = cart.items;
  const { cashItems, elecItems, cashTotal, elecTotal } = paymentBreakdown;

  const renderRow = (it) => (
    <tr key={it.product_id} className="border-b border-black/10 align-top" data-testid={`cart-item-${it.product_id}`}>
      <td className="p-3">
        <div className="flex items-start gap-3">
          {it.image_url && (
            <img src={resolveImage(it.image_url)} alt={it.name} className="w-14 h-14 object-cover border border-ink shrink-0" />
          )}
          <div>
            <div className="font-bold">{it.name}</div>
            {it.brand && <div className="text-xs text-black/60">{it.brand}</div>}
            {unitSummary(it) && <div className="text-[11px] text-black/60 mt-1" data-testid={`cart-size-${it.product_id}`}>{unitSummary(it)}</div>}
            <div className="mt-1"><PaymentBadge method={it.payment_method} /></div>
          </div>
        </div>
      </td>
      <td className="p-3">
        <input type="number" min={it.min_order_qty || 1} value={it.quantity}
               onChange={e => setQty(it.product_id, parseInt(e.target.value) || 0)}
               className="field w-20 py-1 text-sm" data-testid={`cart-qty-${it.product_id}`} />
      </td>
      <td className="p-3 text-right">{currency}{it.wholesale_price.toFixed(2)}</td>
      <td className="p-3 text-right font-bold">{currency}{(it.wholesale_price * it.quantity).toFixed(2)}</td>
      <td className="p-3 text-right">
        <button onClick={() => remove(it.product_id)} className="text-[11px] underline text-soda" data-testid={`cart-remove-${it.product_id}`}>Remove</button>
      </td>
    </tr>
  );

  const table = (label, rows, testid) => (
    <div className="border border-ink overflow-x-auto" data-testid={testid}>
      <div className="p-3 bg-secondary border-b border-ink overline flex items-center justify-between">
        <span>{label}</span>
        <span className="text-[10px] font-bold">{rows.length} product{rows.length === 1 ? "" : "s"}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ink bg-ink text-paper">
            <th className="text-left p-3 overline">Product</th>
            <th className="text-left p-3 overline">Qty</th>
            <th className="text-right p-3 overline">Unit</th>
            <th className="text-right p-3 overline">Line total</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <div className="pt-28 md:pt-32 pb-16 px-6 md:px-12 lg:px-16" data-testid="cart-page">
        <div className="overline text-black/50 mb-2">Wholesale cart</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">Your cart.</h1>

        {items.length === 0 ? (
          <div className="mt-8 border border-ink p-8 md:p-12 text-center max-w-2xl" data-testid="cart-empty">
            <p className="text-black/60 mb-4">Your cart is empty. Head to the catalog to add wholesale cases.</p>
            <Link to="/catalog" className="btn-primary" data-testid="cart-browse-btn">Browse catalog →</Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-6">
              {cashItems.length > 0 && table("Cash items", cashItems, "cart-cash-section")}
              {elecItems.length > 0 && table("E-Transfer / Cheque items", elecItems, "cart-elec-section")}
            </div>
            <aside className="lg:col-span-4">
              <div className="border border-ink p-5 md:p-6 sticky top-24 bg-paper hard-shadow" data-testid="cart-summary">
                <div className="overline mb-4">Order summary</div>
                <div className="flex justify-between text-sm mb-2"><span>{totals.count} product{totals.count === 1 ? "" : "s"}</span><span>{totals.units} units</span></div>

                {cashItems.length > 0 && (
                  <div className="border-t border-black/10 pt-3 mt-3" data-testid="cart-cash-summary">
                    <div className="flex justify-between text-sm"><span className="font-bold">Cash total</span><span className="font-bold">{currency}{cashTotal.toFixed(2)}</span></div>
                    <div className="text-[11px] text-black/60 mt-1">{TAX_NOTICE_CASH}</div>
                  </div>
                )}
                {elecItems.length > 0 && (
                  <div className="border-t border-black/10 pt-3 mt-3" data-testid="cart-elec-summary">
                    <div className="flex justify-between text-sm"><span className="font-bold">E-Transfer / Cheque subtotal</span><span className="font-bold">{currency}{elecTotal.toFixed(2)}</span></div>
                    <div className="text-[11px] text-safety font-bold mt-1" data-testid="cart-tax-notice">⚑ {TAX_NOTICE_ELEC}</div>
                  </div>
                )}

                <div className="flex justify-between text-lg font-bold border-t-2 border-ink pt-3 mt-3">
                  <span>Combined total (before tax)</span>
                  <span data-testid="cart-subtotal">{currency}{totals.subtotal.toFixed(2)}</span>
                </div>

                <button onClick={() => nav("/checkout")} className="btn-primary w-full mt-6" data-testid="cart-checkout-btn">
                  Proceed to checkout →
                </button>
                <Link to="/catalog" className="btn-ghost w-full mt-3 block text-center" data-testid="cart-continue">Continue shopping</Link>
              </div>
            </aside>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
